// MatchNode.jsx - Nodo partita interno per compound nodes
// Rappresenta una singola partita con 2 input (squadre) e 2 output (vincente/perdente)
import React, { useState, useRef, useEffect } from 'react';

const MatchNode = ({
  match,
  selected,
  onSelect,
  onDelete,
  onStartConnection,
  onEndConnection,
  onUpdateName,
  fascia = 1, // Fascia ereditata dal nodo Dirette padre
  connecting,
  connectingPort,
  parentOffset = { x: 0, y: 0 },
  scale = 1,
  onDragStart,
  isDragging = false
}) => {
  const WIDTH = 120;
  const HEIGHT = 60;
  const HEADER_HEIGHT = 18;

  // Stato per editing inline
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(match.name || 'Match');
  const inputRef = useRef(null);

  // Posizione assoluta = posizione relativa + offset del parent
  const absX = match.x + parentOffset.x;
  const absY = match.y + parentOffset.y;

  // Focus sull'input quando entra in modalità editing
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Aggiorna editedName quando cambia match.name
  useEffect(() => {
    setEditedName(match.name || 'Match');
  }, [match.name]);

  // Configurazione porte
  const ports = {
    inputs: [
      { id: 'in-1', label: 'S1', y: HEIGHT * 0.3 },  // Squadra 1
      { id: 'in-2', label: 'S2', y: HEIGHT * 0.7 }   // Squadra 2
    ],
    outputs: [
      { id: 'winner', label: 'V', y: HEIGHT * 0.3, color: '#4CAF50' },  // Vincente
      { id: 'loser', label: 'P', y: HEIGHT * 0.7, color: '#f44336' }    // Perdente
    ]
  };

  const handleMouseDown = (e) => {
    if (isEditing) return; // Non fare drag mentre si edita
    e.stopPropagation();
    onSelect?.(match, e);
    // Inizia il drag se è disponibile il handler
    if (onDragStart) {
      onDragStart(match.id, e);
    }
  };

  const handleHeaderDoubleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditing(true);
  };

  const handlePortClick = (e, portId, portType) => {
    e.stopPropagation();
    e.preventDefault();

    if (connecting) {
      // Stiamo completando una connessione
      onEndConnection?.(match.id, portId, portType);
    } else {
      // Stiamo iniziando una connessione
      onStartConnection?.(match.id, portId, portType);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(match.id);
  };

  // === EDITING HANDLERS ===

  const saveNameChange = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== match.name) {
      onUpdateName?.(match.id, trimmedName);
    } else {
      setEditedName(match.name || 'Match');
    }
    setIsEditing(false);
  };

  const handleInputKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      saveNameChange();
    } else if (e.key === 'Escape') {
      setEditedName(match.name || 'Match');
      setIsEditing(false);
    }
  };

  const handleInputBlur = () => {
    saveNameChange();
  };

  const handleInputChange = (e) => {
    setEditedName(e.target.value);
  };

  // Determina se una porta è evidenziata durante il collegamento
  const isPortHighlighted = (portId, portType) => {
    if (!connecting) return false;
    // Input può ricevere solo da output e viceversa
    return connectingPort?.type !== portType;
  };

  return (
    <g className={`match-node ${selected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}>
      {/* Sfondo nodo */}
      <rect
        x={absX}
        y={absY}
        width={WIDTH}
        height={HEIGHT}
        fill={isDragging ? '#FFB74D' : '#FF9800'}
        stroke={selected ? '#1976D2' : '#E65100'}
        strokeWidth={selected ? 3 : 1.5}
        rx={6}
        onMouseDown={handleMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab', opacity: isDragging ? 0.8 : 1 }}
      />

      {/* Header - area cliccabile per editing */}
      <rect
        x={absX}
        y={absY}
        width={WIDTH}
        height={HEADER_HEIGHT}
        fill="#E65100"
        rx={6}
        onMouseDown={(e) => e.stopPropagation()}
        onDoubleClick={handleHeaderDoubleClick}
        style={{ cursor: 'text' }}
      />
      <rect
        x={absX}
        y={absY + 12}
        width={WIDTH}
        height={6}
        fill="#E65100"
        pointerEvents="none"
      />

      {/* Titolo - testo o input */}
      {isEditing ? (
        <foreignObject
          x={absX + 4}
          y={absY + 2}
          width={WIDTH - 8}
          height={HEADER_HEIGHT - 4}
        >
          <input
            ref={inputRef}
            type="text"
            value={editedName}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={handleInputBlur}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              outline: 'none',
              background: 'rgba(255,255,255,0.9)',
              borderRadius: '3px',
              fontSize: '10px',
              fontWeight: 'bold',
              textAlign: 'center',
              padding: '0 2px',
              boxSizing: 'border-box'
            }}
          />
        </foreignObject>
      ) : (
        <text
          x={absX + WIDTH / 2}
          y={absY + 13}
          textAnchor="middle"
          fill="white"
          fontSize="10"
          fontWeight="bold"
          pointerEvents="none"
        >
          {match.name || 'Match'}
        </text>
      )}

      {/* Badge fascia - solo informativo, ereditato dal nodo Dirette */}
      <g pointerEvents="none">
        <rect
          x={absX + WIDTH / 2 - 14}
          y={absY + 26}
          width={28}
          height={16}
          fill="rgba(255,255,255,0.9)"
          stroke="#E65100"
          strokeWidth="1"
          rx={3}
        />
        <text
          x={absX + WIDTH / 2}
          y={absY + 38}
          textAnchor="middle"
          fill="#E65100"
          fontSize="10"
          fontWeight="bold"
        >
          F{fascia}
        </text>
      </g>

      {/* Porte Input (sinistra) */}
      {ports.inputs.map((port) => (
        <g key={port.id}>
          {/* Etichetta porta */}
          <text
            x={absX + 12}
            y={absY + port.y + 3}
            fill="white"
            fontSize="8"
            fontWeight="bold"
            pointerEvents="none"
          >
            {port.label}
          </text>

          {/* Cerchio porta */}
          <circle
            cx={absX - 6}
            cy={absY + port.y}
            r={isPortHighlighted(port.id, 'input') ? 7 : 5}
            fill={isPortHighlighted(port.id, 'input') ? '#2196F3' : '#FFF'}
            stroke="#E65100"
            strokeWidth="2"
            onClick={(e) => handlePortClick(e, port.id, 'input')}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ cursor: 'crosshair' }}
            className={isPortHighlighted(port.id, 'input') ? 'port-highlighted' : ''}
          />
        </g>
      ))}

      {/* Porte Output (destra) */}
      {ports.outputs.map((port) => (
        <g key={port.id}>
          {/* Etichetta porta */}
          <text
            x={absX + WIDTH - 12}
            y={absY + port.y + 3}
            fill="white"
            fontSize="8"
            fontWeight="bold"
            textAnchor="end"
            pointerEvents="none"
          >
            {port.label}
          </text>

          {/* Cerchio porta */}
          <circle
            cx={absX + WIDTH + 6}
            cy={absY + port.y}
            r={isPortHighlighted(port.id, 'output') ? 7 : 5}
            fill={isPortHighlighted(port.id, 'output') ? '#2196F3' : port.color}
            stroke={port.color}
            strokeWidth="2"
            onClick={(e) => handlePortClick(e, port.id, 'output')}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ cursor: 'crosshair' }}
            className={isPortHighlighted(port.id, 'output') ? 'port-highlighted' : ''}
          />
        </g>
      ))}

      {/* Pulsante elimina (visibile quando selezionato) */}
      {selected && !isEditing && (
        <g className="delete-button" onClick={handleDelete} style={{ cursor: 'pointer' }}>
          <circle
            cx={absX + WIDTH - 8}
            cy={absY - 8}
            r="10"
            fill="#f44336"
            stroke="#fff"
            strokeWidth="2"
          />
          <text
            x={absX + WIDTH - 8}
            y={absY - 5}
            textAnchor="middle"
            fill="white"
            fontSize="12"
            fontWeight="bold"
            pointerEvents="none"
          >
            ×
          </text>
        </g>
      )}

      {/* Indicatore di connessione attiva */}
      {connecting === match.id && (
        <rect
          x={absX - 4}
          y={absY - 4}
          width={WIDTH + 8}
          height={HEIGHT + 8}
          fill="none"
          stroke="#2196F3"
          strokeWidth="2"
          strokeDasharray="5,5"
          rx={8}
          className="connecting-indicator"
          pointerEvents="none"
        />
      )}
    </g>
  );
};

// Costanti esportate per calcoli esterni
MatchNode.WIDTH = 120;
MatchNode.HEIGHT = 60;
MatchNode.PORTS = {
  inputs: [
    { id: 'in-1', label: 'S1', yRatio: 0.3 },
    { id: 'in-2', label: 'S2', yRatio: 0.7 }
  ],
  outputs: [
    { id: 'winner', label: 'V', yRatio: 0.3 },
    { id: 'loser', label: 'P', yRatio: 0.7 }
  ]
};

export default MatchNode;
